import { Alert, Input, Modal, Skeleton, Space, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ROLE_LABEL, type Role, type UserDeletionImpact } from '@titularis/api-client';
import { api } from '../../api';

/**
 * Excluir uma conta, com tudo o que sai e o que fica à vista.
 *
 * Veio da tela de Administradores, e chegou aqui sem uma linha de mudança na
 * regra: a confirmação continua exigindo o e-mail exato, o impacto continua
 * vindo do servidor, e os três blocos continuam separados porque são três
 * razões diferentes com três consequências diferentes.
 *
 * O que mudou é o lugar. Ali a exclusão era uma coluna de uma tabela de
 * cinquenta linhas, e o único contexto sobre a pessoa era o nome dela; aqui ela
 * fica ao lado de tudo o que a conta fez - que é a informação que decide se
 * excluir é mesmo o que se quer fazer, em vez de suspender.
 */
export function ExclusaoDaConta({
  alvo,
  aberto,
  aoFechar,
}: {
  alvo: { id: string; nome: string; email: string; papel: string };
  aberto: boolean;
  aoFechar: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmacao, setConfirmacao] = useState('');

  const impacto = useQuery({
    queryKey: ['deletion-impact', alvo.id],
    queryFn: () => api.userDeletionImpact(alvo.id),
    enabled: aberto,
  });

  const excluir = useMutation({
    mutationFn: () => api.deleteUser(alvo.id),
    onSuccess: async () => {
      message.success('Conta excluída.');
      aoFechar();
      /*
       * Tudo o que contava esta conta. A aba Origem e a de Acessos também a
       * contavam, e ficavam de fora: com o `staleTime` de 30 s, quem excluía e
       * abria a aba logo depois via o número de antes.
       */
      await queryClient.invalidateQueries({ queryKey: ['pessoas'] });
      await queryClient.invalidateQueries({ queryKey: ['resumo-das-pessoas'] });
      await queryClient.invalidateQueries({ queryKey: ['funil-por-origem'] });
      await queryClient.invalidateQueries({ queryKey: ['assinantes'] });
      // A ficha deixou de existir: ficar nela mostraria um 404 no lugar de uma tela.
      void navigate('/pessoas', { replace: true });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  // A confirmação por e-mail é o duplo-check: excluir uma conta apaga o uso
  // pessoal dela por inteiro, então exige digitar o e-mail exato.
  const confirmado =
    impacto.isSuccess && confirmacao.trim().toLowerCase() === alvo.email.toLowerCase();

  function fechar() {
    setConfirmacao('');
    aoFechar();
  }

  return (
    <Modal
      open={aberto}
      title={`Excluir a conta de ${alvo.nome}?`}
      okText="Excluir conta"
      okButtonProps={{ danger: true, disabled: !confirmado, loading: excluir.isPending }}
      cancelText="Cancelar"
      onOk={() => excluir.mutate()}
      onCancel={fechar}
      destroyOnHidden
    >
      <ImpactoDaExclusao
        alvo={alvo}
        impacto={impacto}
        confirmacao={confirmacao}
        setConfirmacao={setConfirmacao}
        confirmado={confirmado}
      />
    </Modal>
  );
}

/** Conteúdo do modal: o que sai, o que fica e a confirmação. */
function ImpactoDaExclusao({
  alvo,
  impacto,
  confirmacao,
  setConfirmacao,
  confirmado,
}: {
  alvo: { email: string; papel: string };
  impacto: { isPending: boolean; error: unknown; data?: UserDeletionImpact };
  confirmacao: string;
  setConfirmacao: (valor: string) => void;
  confirmado: boolean;
}) {
  if (impacto.isPending) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (impacto.error) {
    return <Alert type="error" showIcon title={(impacto.error as Error).message} />;
  }
  if (!impacto.data) return null;

  const {
    willDelete: apagar,
    willPreserve: preservar,
    willPseudonymize: compras,
  } = impacto.data;

  const itensApagar = [
    { n: apagar.answers, label: 'respostas' },
    { n: apagar.commentaryViews, label: 'resoluções abertas' },
    { n: apagar.entitlements, label: 'acessos a módulos' },
    { n: apagar.reportsFiled, label: 'reportes enviados' },
  ].filter((item) => item.n > 0);

  const itensPreservar = [
    { n: preservar.examsCreated, label: 'provas cadastradas' },
    { n: preservar.questionsCreated, label: 'questões cadastradas' },
    { n: preservar.versionsAuthored, label: 'versões de questão escritas' },
    { n: preservar.questionsPublished, label: 'questões publicadas' },
    { n: preservar.reportsResolved, label: 'reportes resolvidos' },
  ].filter((item) => item.n > 0);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="warning"
        showIcon
        title="Esta ação não tem volta."
        description={`${alvo.email} · ${ROLE_LABEL[alvo.papel as Role]}`}
      />

      <div>
        <Typography.Text strong>Será apagado</Typography.Text>
        <ul style={{ marginBottom: 0, paddingInlineStart: 20 }}>
          <li>A conta, as credenciais e {apagar.sessions} sessão(ões) ativa(s)</li>
          {itensApagar.map((item) => (
            <li key={item.label}>
              {item.n} {item.label}
            </li>
          ))}
        </ul>
      </div>

      {itensPreservar.length > 0 ? (
        <div>
          <Typography.Text strong>Será preservado</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Conteúdo da plataforma feito por esta conta. Continua no ar; a autoria passa a
            exibir "Conta excluída".
          </Typography.Paragraph>
          <ul style={{ marginBottom: 0, paddingInlineStart: 20 }}>
            {itensPreservar.map((item) => (
              <li key={item.label}>
                {item.n} {item.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
        O histórico de compra tem bloco próprio, e não entra em "Será
        preservado". São duas razões diferentes com duas consequências
        diferentes: o conteúdo fica porque é da plataforma e só perde o nome do
        autor; a venda fica porque a lei manda guardar. Somar os dois faria o
        Dono ler "preservado" e entender "é nosso", quando o certo é "não posso
        apagar, e por isso quebro o vínculo".

        Até 16/09/2026 esta tela não dizia nada sobre compra, e o billing
        sobrevivia inteiro à exclusão sem que ninguém fosse avisado.
      */}
      {compras.orders > 0 ? (
        <div>
          <Typography.Text strong>Fica, por obrigação legal</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Venda é registro fiscal e não se apaga a pedido: os prazos são de cinco anos
            (CTN, arts. 173 e 174; CDC, art. 27), e a LGPD autoriza conservá-la no art. 16, I.
            O que a exclusão faz é <strong>quebrar o vínculo com a pessoa</strong> e limpar dos
            eventos o que o provedor enviou sobre o comprador. O valor e as datas continuam nos
            relatórios; o nome e o e-mail somem com a conta.
          </Typography.Paragraph>
          <ul style={{ marginBottom: 0, paddingInlineStart: 20 }}>
            <li>
              {compras.orders} pedido(s)
              {compras.paidOrders > 0 ? `, ${compras.paidOrders} pago(s)` : null}
            </li>
            {compras.billingEvents > 0 ? (
              <li>{compras.billingEvents} evento(s) de pagamento</li>
            ) : null}
          </ul>
          <Typography.Paragraph
            type="secondary"
            style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
          >
            Isto é pseudonimização, e não anonimização: a referência do pagamento continua
            guardada, então o provedor ainda sabe quem pagou. O dado segue protegido pela LGPD.
          </Typography.Paragraph>
        </div>
      ) : null}

      <div>
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Para confirmar, digite o e-mail <Typography.Text code>{alvo.email}</Typography.Text>:
        </Typography.Paragraph>
        <Input
          value={confirmacao}
          onChange={(evento) => setConfirmacao(evento.target.value)}
          placeholder="e-mail da conta"
          status={confirmacao && !confirmado ? 'error' : undefined}
          onPressEnter={(evento) => evento.preventDefault()}
        />
      </div>
    </Space>
  );
}
